import React, { useState } from 'react';
import { Modal } from 'antd';
import { Button as BaseButton } from '@packages/utils';
import styled from 'styled-components';
import { DatePicker } from 'components/Base/DatePicker';
import dayjs from 'dayjs';
import PlusMinusInput from 'components/Base/PlusMinusInput/PlusMinusInput';

const StyledEditOrderModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08),
      0px 3px 6px -4px rgba(0, 0, 0, 0.12);
    border: none;
  }

  .ant-modal-header {
    background-color: white;
    padding: 24px;
    margin: 0;
    border-bottom: none;
  }

  .ant-modal-title {
    color: #1b1a1c;
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  .ant-modal-body {
    padding: 0 24px 24px;
  }

  .ant-modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    margin-top: 24px;
    border-top: none;
  }

  .ant-modal-close {
    top: 20px;
    right: 20px;
  }

  .ant-modal-close-x {
    font-size: 42px;
    font-weight: 300;
    width: 24px;
    height: 24px;
    line-height: 24px;
  }
`;

const KitTitle = styled.div`
  margin-bottom: 8px;
  color: #1b1a1c;
  font-family: Lato;
  font-size: 18px;
  line-height: 24px;
`;

const ShipToText = styled.div`
  margin-top: 16px;
  color: #1b1a1c;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  line-height: 23px;
`;

const FormLabels = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

const LabelWrapper = styled.div`
  &:first-child {
    width: 124px;
    margin-right: 10px;
  }

  &:last-child {
    width: 172px;
  }
`;

const Label = styled.label`
  font-size: 16px;
  font-family: Lato;
  color: #1b1a1c;
  font-weight: 400;

  .required {
    color: red;
    margin-left: 4px;
  }
`;

const InputsWrapper = styled.div`
  display: flex;
`;

const DatePickerWrapper = styled.div`
  margin-left: 10px;

  .ant-picker {
    height: 38px;
    display: flex;
    align-items: center;
    width: 172px;

    &.ant-picker-disabled {
      .ant-picker-input > input {
        color: #808080;
      }

      .ant-picker-suffix {
        color: #808080;
      }
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled(BaseButton)`
  border-radius: 8px;
  height: 40px;
  min-width: 120px;
  font-weight: 600;
`;

const SaveButton = styled(BaseButton)`
  border-radius: 8px;
  height: 40px;
  min-width: 120px;
  font-weight: 600;
  background-color: #0077a0;
  border-color: #0077a0;

  &:hover,
  &:focus {
    background-color: #006699;
    border-color: #006699;
  }
`;

export interface EditOrderModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (totalOrder: number) => void;
  kitName: string;
  currentTotalOrder: number;
  currentDate: string | null | undefined;
  address: string;
  isLoading?: boolean;
  hideDatePicker?: boolean;
}

const EditOrderModal: React.FC<EditOrderModalProps> = ({
  visible,
  onClose,
  onSave,
  kitName,
  currentTotalOrder,
  currentDate,
  address,
  isLoading = false,
  hideDatePicker = false,
}) => {
  const [totalOrder, setTotalOrder] = useState<number>(currentTotalOrder || 0);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
    currentDate ? dayjs(currentDate, 'MMM DD, YYYY') : dayjs(),
  );

  const handleSave = () => {
    onSave(totalOrder);
  };

  return (
    <StyledEditOrderModal
      title={hideDatePicker ? 'Edit Inventory' : 'Edit Order'}
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      closeIcon={<span>×</span>}
      width={520}
    >
      <KitTitle>{kitName}</KitTitle>

      <div>
        <FormLabels>
          <LabelWrapper>
            <Label>
              {hideDatePicker ? 'Qty On Hand' : 'Total Order'}
              <span className="required">*</span>
            </Label>
          </LabelWrapper>
          {!hideDatePicker && (
            <LabelWrapper>
              <Label>
                Date<span className="required">*</span>
              </Label>
            </LabelWrapper>
          )}
        </FormLabels>

        <InputsWrapper>
          <PlusMinusInput
            value={totalOrder}
            onChange={setTotalOrder}
            min={0}
            disabled={isLoading}
          />

          {!hideDatePicker && (
            <DatePickerWrapper>
              <DatePicker
                value={selectedDate}
                onChange={date => setSelectedDate(date)}
                style={{ width: '172px' }}
                format="MMMM DD, YYYY"
                disabled
              />
            </DatePickerWrapper>
          )}
        </InputsWrapper>
      </div>

      {!hideDatePicker && <ShipToText>Ship to: {address}</ShipToText>}
      {hideDatePicker && <ShipToText>Organization: {address}</ShipToText>}

      <ModalFooter>
        <CancelButton onClick={onClose} color="primary" disabled={isLoading}>
          Cancel
        </CancelButton>
        <SaveButton
          type="primary"
          onClick={handleSave}
          color="primary"
          loading={isLoading}
          disabled={
            isLoading ||
            totalOrder < 0 ||
            (!hideDatePicker && !selectedDate) ||
            totalOrder === currentTotalOrder
          }
        >
          Save
        </SaveButton>
      </ModalFooter>
    </StyledEditOrderModal>
  );
};

export default EditOrderModal;
